import { Component, Element, h, Host, Listen, Method, Prop, State, Watch } from "@stencil/core";
import { applySchemeDelegate, ColorableComponent, setCssVariablesDelegate } from "../../common/colorable-component";
import { SitelinkSchemeDefinition, SitelinkSchemeName } from "./constants/sitelink-types";
import { SITELINK_SCHEME_CONFIG, SITELINK_SCHEME_MAP } from "./constants/sitelink-constants";
import pfx from "../../utils/style-class-prefix-helper";
import { isDesktopOrLarger } from "../../utils/screen-helper";
import { SitelinkSchemeConfiguration } from "../../constants/colors";
import { BackgroundColor } from "../background/constants/background-types";
import { ConditionalWrapper } from "../../common/conditional-wrapper";
import { StyledComponent } from "../../decorators/styled-component";

// import { InitLogging } from "../../decorators/init-logging";

/**
 * @slot headline-link - Holds a single eon-ui-link element
 * @slot links - Holds any number of eon-ui-link elements.
 */
@Component({
  tag: "eon-ui-sitelink",
  styleUrl: "sitelink.scss",
  shadow: true
})
export class Sitelink implements ColorableComponent<SitelinkSchemeConfiguration<SitelinkSchemeDefinition>> {
  // @InitLogging() sentryTransaction;
  schemeConfig: SitelinkSchemeConfiguration<SitelinkSchemeDefinition> = SITELINK_SCHEME_CONFIG;
  schemeMap = SITELINK_SCHEME_MAP;

  @StyledComponent() component: any;
  @Element() hostElement: HTMLEonUiSitelinkElement;
  private linkElement: HTMLEonUiLinkElement;
  private headlineLinkTitle: any;
  private accordionComponent: HTMLEonUiAccordionElement;

  /**
   * The components active scheme. If {@link scheme} is set, it will be set to the corresponding scheme.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @State() activeScheme: SitelinkSchemeDefinition;
  @State() isDesktopOrLarger: boolean;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: SitelinkSchemeName;

  /**
   * Defines the sitlink accordion pane name.
   */
  @Prop() paneTitle: string;

  componentWillLoad() {
    this.applyScheme();
    this.setLinkAttributes();
  }

  componentDidLoad() {
    this.setCssVariables();
    this.applyAccordionScheme();
  }

  /**
   * This method can be called to re-trigger the automatic background color detection
   */
  @Method()
  async updateScheme(backgroundColor?: BackgroundColor) {
    applySchemeDelegate(this, backgroundColor);
  }

  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
  }

  setLinkAttributes() {
    const links = this.hostElement.querySelectorAll("eon-ui-link");
    links.forEach((link: HTMLEonUiLinkElement, index: number) => {
      link.icon = "chevron_small_right";
      link.iconPosition = "left";

      if (index === 0 && link.slot === "headline-link") {
        this.headlineLinkTitle = link.text;

        /**
         * Below we have updated the Mapping of "Pane-Title" and "Headline link title" for Desktop view
         * "Pane-title" should remain consistent througout the viewport
         * Hence in onViewportChange() we have maintained the title of "header-link" same as that of "pane" on desktop view
         */
        this.onViewportChange();
      } else {
        link.size = "small";
      }
    });
  }

  @Listen("resize", { target: "window" })
  onViewportChange() {
    this.isDesktopOrLarger = isDesktopOrLarger();
    this.linkElement && (this.linkElement.text = this.isDesktopOrLarger ? this.paneTitle : this.headlineLinkTitle);
  }

  @Watch("scheme")
  applyAccordionScheme(): void {
    this.accordionComponent &&
      this.accordionComponent.setAttribute("scheme", this.activeScheme.linkColor.replace("eon-", ""));
  }

  render() {
    return (
      <Host>
        <ConditionalWrapper
          condition={this.isDesktopOrLarger}
          wrapper={() => (
            <div class={pfx("sitelink-wrapper")}>
              <div class={pfx("headline-link")}>
                <slot name="headline-link"></slot>
              </div>
              <div class={pfx("links")}>
                <slot name="links"></slot>
              </div>
            </div>
          )}
          defaultWrapper={() => (
            <eon-ui-accordion
              ref={(el) => (this.accordionComponent = el as HTMLEonUiAccordionElement)}
              hidePaneTitleOnDesktop={true}
              expandOnDesktop={true}
            >
              <eon-ui-accordion-pane pane-title={this.paneTitle} slot="sitelinkPane">
                <div class={pfx("headline-link")}>
                  <slot name="headline-link"></slot>
                </div>
                <div class={pfx("links")}>
                  <slot name="links"></slot>
                </div>
              </eon-ui-accordion-pane>
            </eon-ui-accordion>
          )}
        ></ConditionalWrapper>
      </Host>
    );
  }
}
